/**
 * EditRatesDialog - Modal for editing per-minute rates
 *
 * Features:
 * - Player info header
 * - Minutes stepper
 * - Per-minute rate steppers for each stat
 * - Individual reset buttons per field
 * - "Reset to baseline" button
 * - Cancel / Apply Changes buttons
 */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
} from 'omaha-design-system';
import { X, ArrowClockwise, CaretUp, CaretDown } from '@phosphor-icons/react';
import { PlayerData } from '../../data/types';
import { PlayerRates, PlayerVolumes, PlayerCustomization } from '../../store/types';
import { setPlayerCustomization, resetPlayerCustomization } from '../../store/slice';

// NFL rate keys and the normalized stat codes recomputed after any NFL edit (see buildEVRequest)
const NFL_RATE_KEYS: Array<keyof PlayerRates> = [
  'passYdsPerAtt', 'compPerAtt', 'passTdPerAtt', 'intPerAtt',
  'rushYdsPerAtt', 'rushTdPerAtt', 'catchPerTarget', 'recYdsPerRec', 'recTdPerRec',
];
const ZERO_VOLUMES: PlayerVolumes = { passAtt: 0, rushAtt: 0, targets: 0 };
const NFL_ALL_STATS = [
  'passatt', 'passyds', 'passtds', 'comp', 'int',
  'rushatt', 'rushyds', 'rushtds', 'rec', 'targets', 'recyds', 'rectds',
  'pass+rushyds', 'rush+recyds', 'anytd', 'rush+rectds',
];

interface EditRatesDialogProps {
  open: boolean;
  onClose: () => void;
  player: PlayerData;
  gameId: string;
  eventStartTime: string;
  currentMinutes: number;
  currentRates: PlayerRates;
  baselineMinutes: number;
  baselineRates: PlayerRates;
  /** NFL volume levers (pass attempts / rush attempts / receptions) */
  currentVolumes?: PlayerVolumes;
  baselineVolumes?: PlayerVolumes;
  /** Current league (NBA, NHL, NFL) - determines which stats to show */
  league: string;
  /** Called when Apply is clicked with the stats that changed */
  onApplyWithStats?: (changedStats: string[], appliedCustomization: PlayerCustomization) => void;
  /** Called when Reset to baseline is clicked */
  onReset?: () => void;
}

interface RateRowProps {
  label: string;
  value: number;
  baselineValue: number;
  onChange: (value: number) => void;
  onReset: () => void;
  step?: number;
  min?: number;
  max?: number;
  /** Multiplier for display (e.g., 60 for per-60-min display). Value is stored as per-minute internally. */
  displayMultiplier?: number;
  /** Decimal places for display (default 4 for per-minute, 2 for per-60) */
  decimals?: number;
}

const RateRow: React.FC<RateRowProps> = ({
  label,
  value,
  baselineValue,
  onChange,
  onReset,
  step = 0.01,
  min = 0,
  max = 10,
  displayMultiplier = 1,
  decimals = 4,
}) => {
  // Convert internal per-minute value to display value
  const toDisplay = (v: number) => v * displayMultiplier;
  // Convert display value back to internal per-minute value
  const toInternal = (v: number) => v / displayMultiplier;
  // Format for display
  const formatRate = (v: number) => toDisplay(v).toFixed(decimals);

  // Use string state to allow empty input during editing
  const [inputValue, setInputValue] = useState(formatRate(value));

  // Sync input value when external value changes (e.g., from reset)
  useEffect(() => {
    setInputValue((value * displayMultiplier).toFixed(decimals));
  }, [value, displayMultiplier, decimals]);

  const isModified = Math.abs(value - baselineValue) > 0.0001;

  // Step in display units
  const displayStep = step * displayMultiplier;

  const handleIncrement = () => {
    const newDisplayValue = Math.min(toDisplay(max), toDisplay(value) + displayStep);
    const newInternalValue = toInternal(newDisplayValue);
    onChange(newInternalValue);
    setInputValue(newDisplayValue.toFixed(decimals));
  };

  const handleDecrement = () => {
    const newDisplayValue = Math.max(toDisplay(min), toDisplay(value) - displayStep);
    const newInternalValue = toInternal(newDisplayValue);
    onChange(newInternalValue);
    setInputValue(newDisplayValue.toFixed(decimals));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    setInputValue(rawValue); // Always update the display value

    const parsed = parseFloat(rawValue);
    if (!isNaN(parsed)) {
      // Convert display value to internal, then clamp
      const internalValue = toInternal(parsed);
      onChange(Math.max(min, Math.min(max, internalValue)));
    }
  };

  const handleBlur = () => {
    // On blur, if empty or invalid, reset to current value
    const parsed = parseFloat(inputValue);
    if (isNaN(parsed) || inputValue === '') {
      setInputValue(formatRate(value));
    } else {
      // Convert display to internal, clamp, then convert back for display
      const internalValue = toInternal(parsed);
      const clamped = Math.max(min, Math.min(max, internalValue));
      onChange(clamped);
      setInputValue(toDisplay(clamped).toFixed(decimals));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: isModified
              ? 'rgba(66, 165, 245, 0.1)'
              : 'rgba(255, 255, 255, 0.05)',
            border: isModified
              ? '1px solid #42A5F5'
              : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 1,
            width: 100,
            height: 46,
          }}
        >
          <IconButton
            size="small"
            onClick={handleDecrement}
            sx={{ color: 'text.secondary' }}
          >
            <CaretDown size={16} weight="bold" />
          </IconButton>
          <Box
            component="input"
            type="text"
            inputMode="decimal"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            sx={{
              flex: 1,
              textAlign: 'center',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'text.primary',
              fontFamily: '"Instrument Sans", sans-serif',
              fontSize: '16px', // Must be 16px+ to prevent iOS auto-zoom on focus
              fontWeight: 400,
              outline: 'none',
              width: '100%',
            }}
          />
          <IconButton
            size="small"
            onClick={handleIncrement}
            sx={{ color: 'text.secondary' }}
          >
            <CaretUp size={16} weight="bold" />
          </IconButton>
        </Box>
        <IconButton
          size="small"
          onClick={onReset}
          disabled={!isModified}
          sx={{
            color: isModified ? 'text.primary' : 'text.disabled',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          <ArrowClockwise size={20} weight="regular" />
        </IconButton>
      </Box>
    </Box>
  );
};

const EditRatesDialog: React.FC<EditRatesDialogProps> = ({
  open,
  onClose,
  player,
  gameId,
  eventStartTime,
  currentMinutes,
  currentRates,
  baselineMinutes,
  baselineRates,
  currentVolumes,
  baselineVolumes,
  league,
  onApplyWithStats,
  onReset,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Local state for editing
  const [localMinutes, setLocalMinutes] = useState(currentMinutes);
  const [localRates, setLocalRates] = useState<PlayerRates>(currentRates);
  const [localVolumes, setLocalVolumes] = useState<PlayerVolumes>(currentVolumes ?? ZERO_VOLUMES);
  // String-based state for minutes input to allow empty values during editing
  const [minutesInputValue, setMinutesInputValue] = useState(currentMinutes.toFixed(1));

  // Sync local state when dialog opens. Volumes are compared by value: the object is rebuilt on every
  // refetch, and re-syncing on identity alone would wipe in-progress edits (NBA/NHL included).
  const volumesKey = `${currentVolumes?.passAtt ?? 0}|${currentVolumes?.rushAtt ?? 0}|${currentVolumes?.targets ?? 0}`;
  useEffect(() => {
    if (open) {
      setLocalMinutes(currentMinutes);
      setLocalRates(currentRates);
      setLocalVolumes(currentVolumes ?? ZERO_VOLUMES);
      setMinutesInputValue(currentMinutes.toFixed(1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentMinutes, currentRates, volumesKey]);

  const handleApply = () => {
    // Determine which stats changed
    const changedStats: string[] = [];
    const minutesChanged = Math.abs(localMinutes - currentMinutes) > 0.1;

    const isNBA = league === 'NBA';
    const isNHL = league === 'NHL';
    const isNFL = league === 'NFL';

    if (isNBA) {
      const ptsChanged = Math.abs(localRates.pts - currentRates.pts) > 0.001;
      const rebChanged = Math.abs(localRates.reb - currentRates.reb) > 0.001;
      const astChanged = Math.abs(localRates.ast - currentRates.ast) > 0.001;
      const threesChanged = Math.abs(localRates.threes - currentRates.threes) > 0.001;

      // If minutes changed, all stats are affected
      if (minutesChanged) {
        changedStats.push('points', 'rebounds', 'assists', 'threes');
      } else {
        // Only add stats whose rates changed
        if (ptsChanged) changedStats.push('points');
        if (rebChanged) changedStats.push('rebounds');
        if (astChanged) changedStats.push('assists');
        if (threesChanged) changedStats.push('threes');
      }
    } else if (isNHL) {
      const goalsChanged = Math.abs((localRates.goals ?? 0) - (currentRates.goals ?? 0)) > 0.001;
      const nhlAssistsChanged = Math.abs((localRates.nhlAssists ?? 0) - (currentRates.nhlAssists ?? 0)) > 0.001;
      const sogChanged = Math.abs((localRates.sog ?? 0) - (currentRates.sog ?? 0)) > 0.001;
      const savesChanged = Math.abs((localRates.saves ?? 0) - (currentRates.saves ?? 0)) > 0.001;
      const nhlPointsChanged = Math.abs((localRates.nhlPoints ?? 0) - (currentRates.nhlPoints ?? 0)) > 0.001;
      const blocksChanged = Math.abs((localRates.blocks ?? 0) - (currentRates.blocks ?? 0)) > 0.001;

      // If minutes changed, all stats are affected
      if (minutesChanged) {
        changedStats.push('goals', 'assists', 'sog', 'saves', 'points', 'blocks');
      } else {
        // Only add stats whose rates changed
        if (goalsChanged) changedStats.push('goals');
        if (nhlAssistsChanged) changedStats.push('assists');
        if (sogChanged) changedStats.push('sog');
        if (savesChanged) changedStats.push('saves');
        if (nhlPointsChanged) changedStats.push('points');
        if (blocksChanged) changedStats.push('blocks');
      }
    } else if (isNFL) {
      // Any volume or rate change ripples through combos, so recompute every NFL market.
      const vols = currentVolumes ?? ZERO_VOLUMES;
      const volumesChanged =
        Math.abs(localVolumes.passAtt - vols.passAtt) > 0.01 ||
        Math.abs(localVolumes.rushAtt - vols.rushAtt) > 0.01 ||
        Math.abs(localVolumes.targets - vols.targets) > 0.01;
      const ratesChanged = NFL_RATE_KEYS.some(
        (k) => Math.abs((localRates[k] ?? 0) - (currentRates[k] ?? 0)) > 1e-6
      );
      if (volumesChanged || ratesChanged) {
        changedStats.push(...NFL_ALL_STATS);
      }
    }

    const customization: PlayerCustomization = {
      gameId,
      eventStartTime,
      minutes: localMinutes,
      rates: localRates,
      ...(isNFL ? { volumes: localVolumes } : {}),
    };
    dispatch(setPlayerCustomization({ playerId: player.id, customization }));

    // Trigger EV calculation for changed stats, priced from the customization just applied
    if (changedStats.length > 0 && onApplyWithStats) {
      onApplyWithStats(changedStats, customization);
    }

    onClose();
  };

  const handleResetToBaseline = () => {
    dispatch(resetPlayerCustomization(player.id));
    onReset?.();
    onClose();
  };

  const handleRateChange = (stat: keyof PlayerRates, value: number) => {
    setLocalRates((prev) => ({ ...prev, [stat]: value }));
  };

  const handleResetRate = (stat: keyof PlayerRates) => {
    setLocalRates((prev) => ({ ...prev, [stat]: baselineRates[stat] }));
  };

  // NFL volume levers
  const nflBaseVolumes = baselineVolumes ?? ZERO_VOLUMES;
  const handleVolumeChange = (key: keyof PlayerVolumes, value: number) => {
    setLocalVolumes((prev) => ({ ...prev, [key]: value }));
  };
  const handleResetVolume = (key: keyof PlayerVolumes) => {
    setLocalVolumes((prev) => ({ ...prev, [key]: nflBaseVolumes[key] }));
  };

  const handleMinutesChange = (delta: number) => {
    const newValue = Math.max(0, Math.min(48, localMinutes + delta));
    setLocalMinutes(newValue);
    setMinutesInputValue(newValue.toFixed(1));
  };

  // Restricts input to one decimal place
  const handleMinutesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Allow empty, or valid number format with at most one decimal place
    if (rawValue === '' || /^\d*\.?\d?$/.test(rawValue)) {
      setMinutesInputValue(rawValue);

      const parsed = parseFloat(rawValue);
      if (!isNaN(parsed)) {
        setLocalMinutes(Math.max(0, Math.min(48, parsed)));
      }
    }
  };

  const handleMinutesBlur = () => {
    // On blur, if empty or invalid, reset to current value
    const parsed = parseFloat(minutesInputValue);
    if (isNaN(parsed) || minutesInputValue === '') {
      setMinutesInputValue(localMinutes.toFixed(1));
    } else {
      // Clamp and format the value
      const clamped = Math.max(0, Math.min(48, parsed));
      setLocalMinutes(clamped);
      setMinutesInputValue(clamped.toFixed(1));
    }
  };

  const handleMinutesReset = () => {
    setLocalMinutes(baselineMinutes);
    setMinutesInputValue(baselineMinutes.toFixed(1));
  };

  const isMinutesModified = Math.abs(localMinutes - baselineMinutes) > 0.1;

  const content = (
    <>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: 3,
          pb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Gilroy", sans-serif',
              fontWeight: 900,
              fontSize: '19.2px',
              mb: 0.5,
            }}
          >
            {league === 'NFL' ? 'Edit Volume & Rates' : 'Edit Per-Minute Rates'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              color: 'text.secondary',
            }}
          >
            {league === 'NFL'
              ? 'Attempts and receptions are the volume; the rates are per attempt or per reception. Projections are volume × rate. Reset to baseline to restore the original values.'
              : 'Changes to these per-minute rates will override baseline projections. Reset to baseline to restore their original values.'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.primary' }}>
          <X size={20} weight="bold" />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Player Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            src={player.avatar}
            alt={player.name}
            sx={{ width: 48, height: 48 }}
          />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600 }}
              >
                {player.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: '"Instrument Sans", sans-serif', color: 'text.secondary' }}
              >
                {player.position}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontFamily: '"Instrument Sans", sans-serif', color: 'text.secondary' }}
            >
              {player.team}
            </Typography>
          </Box>
        </Box>

        {/* Minutes Row (NBA/NHL) */}
        {league !== 'NFL' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: '"Instrument Sans", sans-serif', fontWeight: 600 }}
          >
            Minutes
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: isMinutesModified
                  ? 'rgba(66, 165, 245, 0.1)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: isMinutesModified
                  ? '1px solid #42A5F5'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 1,
                width: 105,
                height: 46,
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleMinutesChange(-1)}
                sx={{ color: 'text.secondary' }}
              >
                <CaretDown size={16} weight="bold" />
              </IconButton>
              <Box
                component="input"
                type="text"
                inputMode="decimal"
                value={minutesInputValue}
                onChange={handleMinutesInputChange}
                onBlur={handleMinutesBlur}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'text.primary',
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontSize: '16px', // Must be 16px+ to prevent iOS auto-zoom on focus
                  fontWeight: 400,
                  outline: 'none',
                  width: '100%',
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleMinutesChange(1)}
                sx={{ color: 'text.secondary' }}
              >
                <CaretUp size={16} weight="bold" />
              </IconButton>
            </Box>
            <IconButton
              size="small"
              onClick={handleMinutesReset}
              disabled={!isMinutesModified}
              sx={{
                color: isMinutesModified ? 'text.primary' : 'text.disabled',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ArrowClockwise size={20} weight="regular" />
            </IconButton>
          </Box>
        </Box>
        )}

        {/* NFL volume rows — the football equivalent of minutes */}
        {league === 'NFL' && (
          <>
            {nflBaseVolumes.passAtt > 0 && (
              <RateRow
                label="Pass attempts"
                value={localVolumes.passAtt}
                baselineValue={nflBaseVolumes.passAtt}
                onChange={(v) => handleVolumeChange('passAtt', v)}
                onReset={() => handleResetVolume('passAtt')}
                step={1}
                max={80}
                decimals={1}
              />
            )}
            {nflBaseVolumes.rushAtt > 0 && (
              <RateRow
                label="Rush attempts"
                value={localVolumes.rushAtt}
                baselineValue={nflBaseVolumes.rushAtt}
                onChange={(v) => handleVolumeChange('rushAtt', v)}
                onReset={() => handleResetVolume('rushAtt')}
                step={1}
                max={50}
                decimals={1}
              />
            )}
            {nflBaseVolumes.targets > 0 && (
              <RateRow
                label="Targets"
                value={localVolumes.targets}
                baselineValue={nflBaseVolumes.targets}
                onChange={(v) => handleVolumeChange('targets', v)}
                onReset={() => handleResetVolume('targets')}
                step={0.5}
                max={25}
                decimals={1}
              />
            )}
          </>
        )}

        {/* Rate Rows - League-specific stats */}
        {league === 'NBA' ? (
          <>
            <RateRow
              label="Points / minute"
              value={localRates.pts}
              baselineValue={baselineRates.pts}
              onChange={(v) => handleRateChange('pts', v)}
              onReset={() => handleResetRate('pts')}
            />
            <RateRow
              label="Rebounds / minute"
              value={localRates.reb}
              baselineValue={baselineRates.reb}
              onChange={(v) => handleRateChange('reb', v)}
              onReset={() => handleResetRate('reb')}
            />
            <RateRow
              label="Assists / minute"
              value={localRates.ast}
              baselineValue={baselineRates.ast}
              onChange={(v) => handleRateChange('ast', v)}
              onReset={() => handleResetRate('ast')}
            />
            <RateRow
              label="3-Pointers / minute"
              value={localRates.threes}
              baselineValue={baselineRates.threes}
              onChange={(v) => handleRateChange('threes', v)}
              onReset={() => handleResetRate('threes')}
            />
          </>
        ) : league === 'NHL' ? (
          <>
            <RateRow
              label="Goals / 60 min"
              value={localRates.goals ?? 0}
              baselineValue={baselineRates.goals ?? 0}
              onChange={(v) => handleRateChange('goals', v)}
              onReset={() => handleResetRate('goals')}
              displayMultiplier={60}
              decimals={2}
            />
            <RateRow
              label="Assists / 60 min"
              value={localRates.nhlAssists ?? 0}
              baselineValue={baselineRates.nhlAssists ?? 0}
              onChange={(v) => handleRateChange('nhlAssists', v)}
              onReset={() => handleResetRate('nhlAssists')}
              displayMultiplier={60}
              decimals={2}
            />
            <RateRow
              label="Shots on Goal / 60 min"
              value={localRates.sog ?? 0}
              baselineValue={baselineRates.sog ?? 0}
              onChange={(v) => handleRateChange('sog', v)}
              onReset={() => handleResetRate('sog')}
              displayMultiplier={60}
              decimals={2}
            />
            <RateRow
              label="Blocks / 60 min"
              value={localRates.blocks ?? 0}
              baselineValue={baselineRates.blocks ?? 0}
              onChange={(v) => handleRateChange('blocks', v)}
              onReset={() => handleResetRate('blocks')}
              displayMultiplier={60}
              decimals={2}
            />
            <RateRow
              label="Saves / 60 min"
              value={localRates.saves ?? 0}
              baselineValue={baselineRates.saves ?? 0}
              onChange={(v) => handleRateChange('saves', v)}
              onReset={() => handleResetRate('saves')}
              displayMultiplier={60}
              decimals={2}
            />
            <RateRow
              label="Points / 60 min"
              value={localRates.nhlPoints ?? 0}
              baselineValue={baselineRates.nhlPoints ?? 0}
              onChange={(v) => handleRateChange('nhlPoints', v)}
              onReset={() => handleResetRate('nhlPoints')}
              displayMultiplier={60}
              decimals={2}
            />
          </>
        ) : league === 'NFL' ? (
          <>
            {nflBaseVolumes.passAtt > 0 && (
              <>
                <RateRow
                  label="Yards / pass attempt"
                  value={localRates.passYdsPerAtt ?? 0}
                  baselineValue={baselineRates.passYdsPerAtt ?? 0}
                  onChange={(v) => handleRateChange('passYdsPerAtt', v)}
                  onReset={() => handleResetRate('passYdsPerAtt')}
                  step={0.1}
                  max={20}
                  decimals={2}
                />
                <RateRow
                  label="Completion %"
                  value={localRates.compPerAtt ?? 0}
                  baselineValue={baselineRates.compPerAtt ?? 0}
                  onChange={(v) => handleRateChange('compPerAtt', v)}
                  onReset={() => handleResetRate('compPerAtt')}
                  step={0.005}
                  max={1}
                  displayMultiplier={100}
                  decimals={1}
                />
                <RateRow
                  label="Pass TD % (per attempt)"
                  value={localRates.passTdPerAtt ?? 0}
                  baselineValue={baselineRates.passTdPerAtt ?? 0}
                  onChange={(v) => handleRateChange('passTdPerAtt', v)}
                  onReset={() => handleResetRate('passTdPerAtt')}
                  step={0.0025}
                  max={0.5}
                  displayMultiplier={100}
                  decimals={2}
                />
                <RateRow
                  label="INT % (per attempt)"
                  value={localRates.intPerAtt ?? 0}
                  baselineValue={baselineRates.intPerAtt ?? 0}
                  onChange={(v) => handleRateChange('intPerAtt', v)}
                  onReset={() => handleResetRate('intPerAtt')}
                  step={0.0025}
                  max={0.5}
                  displayMultiplier={100}
                  decimals={2}
                />
              </>
            )}
            {nflBaseVolumes.rushAtt > 0 && (
              <>
                <RateRow
                  label="Yards / carry"
                  value={localRates.rushYdsPerAtt ?? 0}
                  baselineValue={baselineRates.rushYdsPerAtt ?? 0}
                  onChange={(v) => handleRateChange('rushYdsPerAtt', v)}
                  onReset={() => handleResetRate('rushYdsPerAtt')}
                  step={0.1}
                  max={15}
                  decimals={2}
                />
                <RateRow
                  label="Rush TD % (per carry)"
                  value={localRates.rushTdPerAtt ?? 0}
                  baselineValue={baselineRates.rushTdPerAtt ?? 0}
                  onChange={(v) => handleRateChange('rushTdPerAtt', v)}
                  onReset={() => handleResetRate('rushTdPerAtt')}
                  step={0.005}
                  max={1}
                  displayMultiplier={100}
                  decimals={2}
                />
              </>
            )}
            {nflBaseVolumes.targets > 0 && (
              <>
                <RateRow
                  label="Catch % (per target)"
                  value={localRates.catchPerTarget ?? 0}
                  baselineValue={baselineRates.catchPerTarget ?? 0}
                  onChange={(v) => handleRateChange('catchPerTarget', v)}
                  onReset={() => handleResetRate('catchPerTarget')}
                  step={0.01}
                  max={1}
                  decimals={3}
                />
                <RateRow
                  label="Yards / reception"
                  value={localRates.recYdsPerRec ?? 0}
                  baselineValue={baselineRates.recYdsPerRec ?? 0}
                  onChange={(v) => handleRateChange('recYdsPerRec', v)}
                  onReset={() => handleResetRate('recYdsPerRec')}
                  step={0.1}
                  max={40}
                  decimals={2}
                />
                <RateRow
                  label="Rec TD % (per reception)"
                  value={localRates.recTdPerRec ?? 0}
                  baselineValue={baselineRates.recTdPerRec ?? 0}
                  onChange={(v) => handleRateChange('recTdPerRec', v)}
                  onReset={() => handleResetRate('recTdPerRec')}
                  step={0.005}
                  max={1}
                  displayMultiplier={100}
                  decimals={2}
                />
              </>
            )}
          </>
        ) : null}
      </Box>

      <Divider />

      {/* Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowClockwise size={16} weight="regular" />}
          onClick={handleResetToBaseline}
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            textTransform: 'none',
            color: 'text.primary',
          }}
        >
          Reset to baseline
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="text"
            onClick={onClose}
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              textTransform: 'none',
              color: 'text.primary',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              textTransform: 'none',
              backgroundColor: '#42A5F5',
              color: 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                backgroundColor: '#1E88E5',
              },
            }}
          >
            Apply Changes
          </Button>
        </Box>
      </Box>
    </>
  );

  // Use Drawer on mobile, Dialog on desktop
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: '#343536',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '90vh',
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#343536',
          borderRadius: 2,
        },
      }}
    >
      {content}
    </Dialog>
  );
};

export default EditRatesDialog;
